import { Box, Typography, alpha } from '@mui/material';
import { memo } from 'react';
import { TakeoffAssembly } from '../../types/AssemblyItemType';

// Accept the selectedAssembly and other required props
export const MeasurementsDisplay = memo(
  ({
    assembly,
    selectedFilter,
    theme,
  }: {
    assembly?: TakeoffAssembly; // The specific assembly to display measurements for
    selectedFilter: string;
    theme: any;
  }) => {
    const shouldShowAll = selectedFilter === 'all';
    const shouldShowCount = shouldShowAll || selectedFilter === 'count';
    const shouldShowLength =
      (shouldShowAll &&
        (assembly?.assemblymeasurementtype === 'length' ||
          assembly?.assemblymeasurementtype === 'area')) ||
      selectedFilter === 'length';
    const shouldShowArea =
      (shouldShowAll && assembly?.assemblymeasurementtype === 'area') || selectedFilter === 'area';

    // Get count, length, area directly from assembly props
    const count = assembly?.pointcount || 0;
    const length = assembly?.segmentlength || 0;
    const area = assembly?.measuredarea || 0;

    // Get colors directly from theme to ensure they update
    const primaryColor = theme.palette.text.primary;
    const secondaryColor = theme.palette.text.secondary;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          alignItems: 'flex-end',
          justifyContent: 'flex-start',
          py: 0.5,
          '& .measurement': {
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: `${secondaryColor} !important`,
            fontSize: '0.75rem',
            lineHeight: 1.2,
            minHeight: '1.2em',
            justifyContent: 'flex-end',
            width: '100%',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.05),
              px: 0.5,
            },
          },
          '& .value': {
            color: `${primaryColor} !important`,
            fontWeight: 500,
          },
        }}
      >
        {shouldShowCount && (
          <Typography
            component="span"
            className="measurement"
            sx={{
              color: `${secondaryColor} !important`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              pb: 0.25,
            }}
          >
            <span className="value" style={{ color: primaryColor }}>
              {count || 0}
            </span>
            <span style={{ color: secondaryColor }}>point{count > 1 ? 's' : ''}</span>
          </Typography>
        )}
        {shouldShowLength && length > 0 && (
          <Typography
            component="span"
            className="measurement"
            sx={{
              color: `${secondaryColor} !important`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              pb: 0.25,
            }}
          >
            <span className="value" style={{ color: primaryColor }}>
              {Math.round(length)}
            </span>
            <span style={{ color: secondaryColor }}>{length > 1 ? 'feet' : 'foot'}</span>
          </Typography>
        )}
        {shouldShowArea && area > 0 && (
          <Typography
            component="span"
            className="measurement"
            sx={{
              color: `${secondaryColor} !important`,
            }}
          >
            <span className="value" style={{ color: primaryColor }}>
              {area.toFixed(2)}
            </span>
            <span style={{ color: secondaryColor }}>sqft</span>
          </Typography>
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for better memoization
    return (
      prevProps.assembly?.pointcount === nextProps.assembly?.pointcount &&
      prevProps.assembly?.segmentlength === nextProps.assembly?.segmentlength &&
      prevProps.assembly?.measuredarea === nextProps.assembly?.measuredarea &&
      prevProps.selectedFilter === nextProps.selectedFilter &&
      prevProps.theme.palette.mode === nextProps.theme.palette.mode
    );
  }
);
